import { apiClient } from "../utils/apiClient";
import { ORGANIZER_ACCESS_URL } from "../config/api";

// ms-audience-access-control, docs/door-validation-decision.md
// UNVERIFIED: decided offline by a phone that did not have the ticket in its list (not admitted)
export type ScanResult = 'ACCEPTED' | 'ALREADY_USED' | 'VOID' | 'NOT_FOUND' | 'UNVERIFIED';
export type ScanMethod = 'QR' | 'MANUAL';

export interface PairingCode {
    // XXXX-XXXX, typed in the scanner app when its camera cannot read the QR
    code: string;
    expiresAt: string;
    // PNG data URI of the pairing QR
    qrImage: string;
}

export interface ScannerDevice {
    deviceId: string;
    name: string;
    pairedAt: string;
    lastSeenAt?: string;
    // Last list download (offline-first); absent for a phone that never synced (validates online)
    lastSyncAt?: string;
    // Scans the phone reported still queued at its last sync
    pendingScans: number;
    // Revoked by the organizer or past the event's access end
    inactive: boolean;
    scans: number;
    admitted: number;
}

export interface StageAttendance {
    stageName: string;
    issued: number;
    entered: number;
}

export interface RecentScan {
    at: string;
    deviceName: string;
    result: ScanResult;
    method: ScanMethod;
    code?: string;
    stageName?: string;
    // Decided by the phone without connection and uploaded later
    offline: boolean;
}

// A ticket admitted more than once: two offline gates let in copies of the same QR. The first entry counts
export interface Collision {
    code?: string;
    stageName?: string;
    entries: Array<{ deviceName: string; at: string; method: ScanMethod; offline: boolean }>;
}

export interface AccessOverview {
    event: { eventId: string; name: string; startDate?: string; location?: string };
    // Event end + 12 h: pairing codes and phones stop working
    accessEnd: string;
    // False once the event is cancelled or its access ended
    accessOpen: boolean;
    pairingCode?: PairingCode | null;
    devices: ScannerDevice[];
    stages: StageAttendance[];
    issued: number;
    entered: number;
    // Scans of tickets that had already entered
    duplicates: number;
    // Scans of codes that are not tickets of this event
    notFound: number;
    // Offline scans of tickets missing in the phone's list: not admitted
    unverified: number;
    collisionCount: number;
    // The latest 20
    collisions: Collision[];
    recentScans: RecentScan[];
}

/** `status`: HTTP status; `code`: the `status` field of the body (NOT_FOUND, ACCESS_CLOSED, UNAVAILABLE). */
export class AccessRequestError extends Error {
    constructor(public readonly status: number, public readonly code?: string) {
        super('No fue posible completar la solicitud de control de acceso');
        this.name = 'AccessRequestError';
    }
}

const fail = async (response: Response): Promise<never> => {
    const body = await response.json().catch(() => ({}));
    throw new AccessRequestError(response.status, body?.status);
};

export const organizerAccessService = {
    async getAccess(eventId: string): Promise<AccessOverview> {
        const response = await apiClient(`${ORGANIZER_ACCESS_URL}/events/${encodeURIComponent(eventId)}/access`, { cache: 'no-store' });
        if (!response.ok) return fail(response);
        return await response.json();
    },

    // The previous code stops working; phones already paired keep working
    async createPairingCode(eventId: string): Promise<PairingCode> {
        const response = await apiClient(`${ORGANIZER_ACCESS_URL}/events/${encodeURIComponent(eventId)}/pairing-code`, { method: 'POST' });
        if (!response.ok) return fail(response);
        return await response.json();
    },

    async revokeDevice(deviceId: string): Promise<void> {
        const response = await apiClient(`${ORGANIZER_ACCESS_URL}/devices/${encodeURIComponent(deviceId)}/revoke`, { method: 'POST' });
        if (!response.ok) return fail(response);
    }
};
